import { useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Card } from "./ui/card";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, Paintbrush, Home, Layers, PaintBucket, Fence } from "lucide-react";
import axios from "axios";
import { ProjectTypeStep } from "./quote/ProjectTypeStep";
import { ProjectDetailsStep } from "./quote/ProjectDetailsStep";
import { ContactInfoStep } from "./quote/ContactInfoStep";
import { FileUploadStep } from "./quote/FileUploadStep";
import { QuoteSuccess } from "./quote/QuoteSuccess";
import { QuoteSidebar } from "./quote/QuoteSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_COUNT = 5;
const TOTAL_STEPS = 4;
const STEP_LABELS = ["Service Type", "Details", "Contact", "Upload"];

const projectTypes = [
  { id: "interior-painting", icon: Paintbrush, title: "Interior Painting", description: "Walls, ceilings, trim, cabinets & more" },
  { id: "exterior-painting", icon: Home, title: "Exterior Painting", description: "House exterior, stucco, & weather-resistant finishes" },
  { id: "popcorn-ceiling", icon: Layers, title: "Popcorn Ceiling Removal", description: "Texture removal & smooth ceiling finishes" },
  { id: "garage-floor", icon: PaintBucket, title: "Garage Floor Coating", description: "Epoxy & polyaspartic floor coatings" },
  { id: "deck-staining", icon: Fence, title: "Deck Painting & Staining", description: "Deck restoration, staining & sealing" },
];

const initialFormData = (preselected) => ({
  project_type: preselected || "",
  project_description: "",
  property_size: "",
  timeline: "",
  budget_range: "",
  full_name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  preferred_contact: "email",
  has_files: false,
});

export const QuoteForm = () => {
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState(() => initialFormData(searchParams.get("service")));

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const isValidType = file.type.startsWith("image/") || file.type === "application/pdf" ||
        file.type === "application/msword" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      return isValidType && file.size <= MAX_FILE_SIZE_BYTES;
    });
    if (validFiles.length !== files.length) toast.error("Some files were rejected. Max 10MB, images/PDF/DOC only.");
    setUploadedFiles((prev) => [...prev, ...validFiles].slice(0, MAX_FILE_COUNT));
    setFormData((prev) => ({ ...prev, has_files: true }));
  }, []);

  const removeFile = useCallback((index) => {
    setUploadedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setFormData((p) => ({ ...p, has_files: false }));
      return updated;
    });
  }, []);

  const validateStep = (s) => {
    if (s === 1) return formData.project_type !== "";
    if (s === 2) return formData.project_description.trim() !== "";
    if (s === 3) return formData.full_name.trim() !== "" && formData.email.trim() !== "" && formData.phone.trim() !== "";
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep((p) => Math.min(p + 1, TOTAL_STEPS));
    else toast.error("Please complete all required fields");
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Get the service title for the email
      const serviceTitle = projectTypes.find(p => p.id === formData.project_type)?.title || formData.project_type;
      
      // Submit to Web3Forms for email notification
      const web3FormsData = {
        access_key: "f1efe876-6607-4def-ab09-05f4daff61fb",
        subject: `New Quote Request: ${serviceTitle}`,
        from_name: "Roll Orlando Website",
        // Contact Info
        name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        // Address
        address: formData.address || "Not provided",
        city: formData.city || "Not provided",
        state: formData.state || "FL",
        zip_code: formData.zip_code || "Not provided",
        preferred_contact: formData.preferred_contact,
        // Project Details
        service_type: serviceTitle,
        project_description: formData.project_description,
        property_size: formData.property_size || "Not specified",
        timeline: formData.timeline || "Not specified",
        budget_range: formData.budget_range || "Not specified",
        // Files info
        files_attached: uploadedFiles.length > 0 ? `Yes (${uploadedFiles.length} files: ${uploadedFiles.map(f => f.name).join(", ")})` : "No",
      };

      const web3Response = await axios.post("https://api.web3forms.com/submit", web3FormsData);
      
      if (web3Response.data.success) {
        // Also save to our backend for record keeping
        try {
          await axios.post(`${API}/quotes`, { ...formData, files_count: uploadedFiles.length, file_names: uploadedFiles.map((f) => f.name) });
        } catch (backendError) {
          // Don't fail if backend save fails, web3forms already sent the email
          console.log("Backend save failed, but email was sent");
        }
        
        setIsSubmitted(true);
        toast.success("Quote request submitted successfully!");
      } else {
        throw new Error("Web3Forms submission failed");
      }
    } catch {
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) return <QuoteSuccess />;

  return (
    <div data-testid="quote-form-page" className="min-h-screen pt-24 pb-16 bg-brand-ice">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">Free Estimate</span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-brand-navy">Get Your Quote</h1>
          <p className="font-body text-lg text-brand-slate mt-2">Tell us about your project and we'll provide a detailed estimate</p>
        </div>

        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEP_LABELS.map((label, idx) => (
              <span key={label} className={`text-xs sm:text-sm font-medium ${idx + 1 <= step ? "text-brand-orange" : "text-brand-slate"}`}>{label}</span>
            ))}
          </div>
          <Progress value={(step / TOTAL_STEPS) * 100} className="h-2 bg-brand-slate/20" />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="bg-white border border-border rounded-sm p-8">
              {step === 1 && <ProjectTypeStep projectTypes={projectTypes} formData={formData} onInputChange={handleInputChange} />}
              {step === 2 && <ProjectDetailsStep formData={formData} onInputChange={handleInputChange} />}
              {step === 3 && <ContactInfoStep formData={formData} onInputChange={handleInputChange} />}
              {step === 4 && <FileUploadStep uploadedFiles={uploadedFiles} fileInputRef={fileInputRef} onFileUpload={handleFileUpload} onRemoveFile={removeFile} />}

              <div className="flex justify-between mt-8 pt-6 border-t border-border">
                {step > 1 ? (
                  <Button type="button" variant="outline" onClick={() => setStep((p) => Math.max(p - 1, 1))} data-testid="prev-btn" className="border-brand-navy text-brand-navy font-heading font-bold uppercase hover:bg-brand-navy hover:text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                ) : <div />}

                {step < TOTAL_STEPS ? (
                  <Button type="button" onClick={handleNext} data-testid="next-btn" className="bg-brand-navy text-white font-heading font-bold uppercase hover:bg-brand-navy/90">
                    Next Step <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button type="button" onClick={handleSubmit} disabled={isSubmitting} data-testid="submit-quote-btn" className="bg-brand-orange text-white font-heading font-bold uppercase hover:bg-brand-orange/90">
                    {isSubmitting ? "Submitting..." : "Submit Quote Request"} <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </Card>
          </div>

          <QuoteSidebar />
        </div>
      </div>
    </div>
  );
};
